// src/pages/drone/buah-alami/utils/importDataDroneBuahAlami.js
import * as XLSX from "xlsx";
import { ref, push, update } from "firebase/database";
import {
    formatDateToDDMMYYYY,
    formatValueAsIs
} from "../../../../utils/formatters-master";

/**
 * Helper untuk import data Excel ke Firebase untuk Drone Buah Alami
 * @param {File} file - File Excel yang akan diimport
 * @param {object} database - Instance Firebase database
 * @param {function} onValuePromise - Fungsi helper untuk membaca data dari Firebase
 * @returns {Promise<object>} - Hasil import { importedCount, updatedCount, ignoredCount, error }
 */
export const importExcelToFirebase = async (file, database, onValuePromise) => {
    const reader = new FileReader();
    
    return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                
                // Gunakan raw: true untuk mendapatkan nilai mentah
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: true });
                
                const dataStartRow = 5; // Data dimulai dari baris 7 (index 6)
                
                const columnMapping = {
                    id_lokasi: 0,        // Kolom B (index 1)
                    tanggal_terbang: 3,   // Kolom E (index 4)
                    tanggal_selesai_identifikasi: 4, // Kolom F (index 5)
                    early_cones: 5,       // Kolom G (index 6)
                    dry_petals: 6,        // Kolom H (index 7)
                    buah_alami: 7,        // Kolom I (index 8)
                    populasi_per_ha: 8    // Kolom J (index 9)
                };

                console.log("Sample raw data dari Excel (Drone Buah Alami):");
                if (jsonData.length > dataStartRow) {
                    console.log("Row 7 raw:", jsonData[dataStartRow]);
                    console.log("id_lokasi:", jsonData[dataStartRow][columnMapping.id_lokasi]);
                    console.log("early_cones:", jsonData[dataStartRow][columnMapping.early_cones]);
                }

                const droneRef = ref(database, "db_drone/tb_buah_alami");
                const existingDroneSnapshot = await onValuePromise(droneRef);
                const existingDroneData = existingDroneSnapshot.val() || {};

                const existingDroneMap = new Map();
                Object.keys(existingDroneData).forEach(fbKey => {
                    const record = existingDroneData[fbKey];
                    if (record.id_lokasi) {
                        existingDroneMap.set(record.id_lokasi.toString().trim(), fbKey);
                    }
                });

                const importTimestamp = new Date().toISOString();

                let importedCount = 0;
                let updatedCount = 0;
                let ignoredCount = 0;

                // Proses data
                for (let i = dataStartRow; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (!row || row.length === 0) {
                        ignoredCount++;
                        continue;
                    }
                    
                    const id_lokasi = row[columnMapping.id_lokasi];
                    const tanggal_terbang = row[columnMapping.tanggal_terbang];
                    const tanggal_selesai_identifikasi = row[columnMapping.tanggal_selesai_identifikasi];
                    const early_cones = row[columnMapping.early_cones];
                    const dry_petals = row[columnMapping.dry_petals];
                    const buah_alami = row[columnMapping.buah_alami];
                    const populasi_per_ha = row[columnMapping.populasi_per_ha];
                    
                    if (!id_lokasi || (typeof id_lokasi === 'string' && id_lokasi.trim() === '')) {
                        ignoredCount++;
                        continue;
                    }

                    const formattedIdLokasi = String(id_lokasi).trim();
                    
                    // Debug: lihat nilai asli sebelum diformat
                    if (importedCount + updatedCount < 2) {
                        console.log(`Data baris ${i + 1} sebelum format:`);
                        console.log(`  id_lokasi:`, id_lokasi);
                        console.log(`  early_cones:`, early_cones);
                        console.log(`  dry_petals:`, dry_petals);
                        console.log(`  buah_alami:`, buah_alami);
                    }

                    // Data dari Excel yang akan disimpan di Firebase
                    const excelDataToUpdate = {
                        id_lokasi: formattedIdLokasi,
                        tanggal_terbang: formatDateToDDMMYYYY(tanggal_terbang),
                        tanggal_selesai_identifikasi: formatDateToDDMMYYYY(tanggal_selesai_identifikasi),
                        early_cones: formatValueAsIs(early_cones),
                        dry_petals: formatValueAsIs(dry_petals),
                        buah_alami: formatValueAsIs(buah_alami),
                        populasi_per_ha: formatValueAsIs(populasi_per_ha),
                        last_imported_at: importTimestamp,
                    };

                    const firebaseKey = existingDroneMap.get(formattedIdLokasi);

                    try {
                        if (firebaseKey) {
                            // Jika data sudah ada, update dengan data baru
                            await update(ref(database, `db_drone/tb_buah_alami/${firebaseKey}`), excelDataToUpdate);
                            updatedCount++;
                        } else {
                            // Jika data baru, push ke database
                            await push(droneRef, excelDataToUpdate);
                            importedCount++;
                        }
                        
                        // Debug setelah save pertama
                        if (importedCount + updatedCount === 1) {
                            console.log("Data pertama yang disimpan (Drone):", firebaseKey ? excelDataToUpdate : "new data");
                        }
                    } catch (error) {
                        console.error(`Error baris ${i + 1}:`, error);
                        ignoredCount++;
                    }
                }

                console.log(`Hasil (Drone): ${importedCount} baru, ${updatedCount} update, ${ignoredCount} abaikan`);

                resolve({
                    success: true,
                    importedCount,
                    updatedCount,
                    ignoredCount,
                    total: importedCount + updatedCount
                });

            } catch (error) {
                console.error("Gagal impor Excel (Drone):", error);
                reject({
                    success: false,
                    error: "Terjadi kesalahan saat mengimpor data dari Excel.",
                    details: error.message
                });
            }
        };

        reader.onerror = () => {
            reject({
                success: false,
                error: "Gagal membaca file."
            });
        };

        reader.readAsArrayBuffer(file);
    });
};

/**
 * Fungsi untuk refresh data setelah import untuk Drone Buah Alami
 * @param {object} database - Instance Firebase database
 * @param {function} onValuePromise - Fungsi helper untuk membaca data dari Firebase
 * @returns {Promise<array>} - Array data yang sudah di-refresh
 */
export const refreshDroneData = async (database, onValuePromise) => {
    const droneRef = ref(database, "db_drone/tb_buah_alami");
    const updatedDroneSnapshot = await onValuePromise(droneRef);
    const updatedDroneData = updatedDroneSnapshot.val() || {};
    
    const updatedLoadedData = [];
    Object.keys(updatedDroneData).forEach(key => {
        const record = updatedDroneData[key];
        updatedLoadedData.push({ 
            firebaseKey: key, 
            // Data dari Excel yang disimpan di Firebase
            id_lokasi: record.id_lokasi || '',
            tanggal_terbang: record.tanggal_terbang || '-',
            tanggal_selesai_identifikasi: record.tanggal_selesai_identifikasi || '-',
            early_cones: record.early_cones || '-',
            dry_petals: record.dry_petals || '-',
            buah_alami: record.buah_alami || '-',
            populasi_per_ha: record.populasi_per_ha || '-',
            // Kolom lainnya akan diisi dari tb_lokasi
            lokasi: record.id_lokasi || '',
            luas_netto: '-',
            tahun: '',
            kepala_kebun: '-',
            kepala_wilayah: '-',
            percentage_buah_alami: '-'
        });
    });
    
    return updatedLoadedData;
};